import { useState } from 'react';
import {
  AlertTriangle, Copy, Check, CreditCard, Landmark, PiggyBank,
  ChevronDown, ChevronRight, DollarSign, ArrowUpRight, Eye
} from 'lucide-react';
import type { DiscoveryLead, DiscoveryLeadTransaction } from '../hooks/useStatements';
import SourcePageViewer from './SourcePageViewer';
import { formatAccountName } from '../utils/accounts';
import { money } from '../lib/money';
import { Button, Card, DateText, MetricCard, Money, Tag } from './ui';

interface MissingAccountsTabProps {
  leads: DiscoveryLead[];
}

interface ViewerState {
  statementId: number;
  pageNumber: number;
  sourceFile: string;
}

function formatDateLong(iso: string | null): string {
  if (!iso) return '—';
  const [year, month, day] = iso.split('-');
  const d = new Date(Number(year), Number(month) - 1, Number(day));
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateShort(iso: string | null): string {
  if (!iso) return '—';
  const [year, month, day] = iso.split('-');
  const d = new Date(Number(year), Number(month) - 1, Number(day));
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function AccountTypeIcon({ type, size = 16 }: { type: string; size?: number }) {
  if (type === 'Credit Card') return <CreditCard size={size} className="text-clay-600" />;
  if (type === 'Savings') return <PiggyBank size={size} className="text-clay-600" />;
  return <Landmark size={size} className="text-clay-600" />;
}

function ConfidenceBadge({ score }: { score: number | null }) {
  if (score === null || score === undefined) return null;
  const tone = score >= 80 ? 'good' : score >= 50 ? 'warn' : 'danger';
  return (
    <Tag tone={tone} size="sm">
      {score}%
    </Tag>
  );
}

function buildTxnLine(t: DiscoveryLeadTransaction): string {
  const dateStr = formatDateLong(t.date).padEnd(14);
  const src = (t.source_bank_label || '').substring(0, 22).padEnd(24);
  const desc = (t.description || '').substring(0, 34).padEnd(36);
  const amt = money(Math.abs(t.amount)).padStart(12);
  const citation = t.source_file && t.source_page
    ? ` [${t.source_file}, p.${t.source_page}]`
    : '';
  return `${dateStr}${src}${desc}${amt}${citation}`;
}

function buildLeadText(lead: DiscoveryLead): string {
  const period =
    lead.first_date && lead.last_date && lead.first_date !== lead.last_date
      ? `${formatDateShort(lead.first_date)} – ${formatDateShort(lead.last_date)}`
      : formatDateShort(lead.first_date);

  const lines = [
    `Account: ${lead.label}`,
    `Type: ${lead.account_type}`,
    `Total Transferred: ${money(Math.abs(lead.total_amount))}`,
    `Number of Transfers: ${lead.transfer_count}`,
    `Period: ${period}`,
    '',
    'Transaction Detail:',
    '-'.repeat(86),
    `${'Date'.padEnd(14)}${'Source Account'.padEnd(24)}${'Description'.padEnd(36)}${'Amount'.padStart(12)}  Citation`,
    '-'.repeat(86),
    ...lead.transactions.map(buildTxnLine),
    '-'.repeat(86),
  ];
  return lines.join('\n');
}

function buildAllText(leads: DiscoveryLead[]): string {
  const sections = [
    'Transfers to Accounts With No Statements on File',
    '='.repeat(86),
    `Total Accounts: ${leads.length}`,
    `Total Transferred: ${money(Math.abs(leads.reduce((s, l) => s + l.total_amount, 0)))}`,
    `Total Transfers: ${leads.reduce((s, l) => s + l.transfer_count, 0)}`,
    '',
    ...leads.flatMap((lead, i) => [
      `${i + 1}. ${buildLeadText(lead)}`,
      '',
    ]),
    'Please produce all account statements for the above accounts.',
  ];
  return sections.join('\n');
}

function CopyButton({ text, label = 'Copy for Discovery' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  function handleClick(e: React.MouseEvent) {
    e.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }
  return (
    <Button
      variant="secondary"
      size="sm"
      icon={copied ? Check : Copy}
      onClick={handleClick}
      className="shrink-0"
    >
      {copied ? 'Copied' : label}
    </Button>
  );
}

interface LeadCardProps {
  lead: DiscoveryLead;
  onViewSource: (state: ViewerState) => void;
}

function LeadCard({ lead, onViewSource }: LeadCardProps) {
  const [expanded, setExpanded] = useState(false);

  const period =
    lead.first_date && lead.last_date && lead.first_date !== lead.last_date
      ? `${formatDateShort(lead.first_date)} – ${formatDateShort(lead.last_date)}`
      : formatDateShort(lead.first_date);

  function handleToggle(e: React.MouseEvent | React.KeyboardEvent) {
    if ('key' in e && e.key !== 'Enter' && e.key !== ' ') return;
    setExpanded(v => !v);
  }

  return (
    <Card padding="none" className="overflow-hidden">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        className="w-full flex items-center gap-4 px-5 py-4 bg-clay-50 hover:bg-clay-100 transition-colors cursor-pointer select-none"
        onClick={handleToggle}
        onKeyDown={handleToggle}
      >
        <div className="shrink-0 text-clay-500">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>

        <div className="flex items-center gap-2 flex-1 min-w-0">
          <AccountTypeIcon type={lead.account_type} size={16} />
          <div className="min-w-0">
            <p className="t-label text-clay-900 leading-tight">{formatAccountName(lead.label)}</p>
            <p className="text-xs text-clay-600 mt-px">{lead.account_type}</p>
          </div>
        </div>

        <div className="hidden sm:block text-right shrink-0 w-36">
          <p className="text-xs text-clay-600">{period}</p>
        </div>

        <div className="text-right shrink-0">
          <Money
            value={Math.abs(lead.total_amount)}
            className="block text-sm font-semibold text-danger"
          />
          <p className="text-xs text-clay-600">
            {lead.transfer_count} transfer{lead.transfer_count !== 1 ? 's' : ''}
          </p>
        </div>

        <Tag tone="danger" size="sm" className="shrink-0">
          No statements
        </Tag>

        <div onClick={e => e.stopPropagation()} className="shrink-0">
          <CopyButton text={buildLeadText(lead)} />
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border bg-surface-card">
          <div className="overflow-x-auto">
            {/* eslint-disable-next-line no-restricted-syntax -- citation buttons + confidence tags per cell and a tfoot total: restyled in place, DataTable cannot host these */}
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="t-eyebrow text-left px-3 py-2 w-28">Date</th>
                  <th className="t-eyebrow text-left px-3 py-2 w-36">Source Account</th>
                  <th className="t-eyebrow text-left px-3 py-2">Description</th>
                  <th className="t-eyebrow text-left px-3 py-2 w-20">Confidence</th>
                  <th className="t-eyebrow text-left px-3 py-2 w-40">Citation</th>
                  <th className="t-eyebrow text-right px-3 py-2 w-28">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle">
                {lead.transactions.map((txn, i) => (
                  <TxnRow key={i} txn={txn} onViewSource={onViewSource} />
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-border">
                  <td colSpan={5} className="px-3 py-2 text-xs font-semibold text-ink-soft text-right">Total:</td>
                  <td className="px-3 py-2 text-xs text-right whitespace-nowrap">
                    <Money value={Math.abs(lead.total_amount)} className="font-semibold text-danger" />
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}

interface TxnRowProps {
  txn: DiscoveryLeadTransaction;
  onViewSource: (state: ViewerState) => void;
}

function TxnRow({ txn, onViewSource }: TxnRowProps) {
  const hasSource = !!(txn.statement_id && txn.source_page);
  const fullCitation = txn.source_file && txn.source_page
    ? `${txn.source_file} (p.${txn.source_page})`
    : '';
  const shortCitation = fullCitation.length > 26
    ? `${fullCitation.substring(0, 23)}…`
    : fullCitation;

  return (
    <tr className="h-11 hover:bg-clay-50 transition-colors">
      <td className="px-3 py-2 text-xs text-ink-soft whitespace-nowrap align-top">
        <DateText value={txn.date} />
      </td>
      <td className="px-3 py-2 text-xs text-ink-soft whitespace-nowrap align-top">
        {txn.source_bank_label ? formatAccountName(txn.source_bank_label) : '—'}
      </td>
      <td className="px-3 py-2 text-xs text-ink-soft align-top">
        <span>{txn.description || '—'}</span>
        {txn.raw_text && txn.raw_text !== txn.description && (
          <p className="text-ink-faint font-mono text-xs mt-1 truncate max-w-xs" title={txn.raw_text}>
            {txn.raw_text.substring(0, 80)}{txn.raw_text.length > 80 ? '…' : ''}
          </p>
        )}
      </td>
      <td className="px-3 py-2 align-top">
        <ConfidenceBadge score={txn.confidence_score} />
      </td>
      <td className="px-3 py-2 align-top">
        {hasSource ? (
          /* eslint-disable-next-line no-restricted-syntax -- inline citation link: no ui/LinkButton primitive exists yet */
          <button
            onClick={() => onViewSource({
              statementId: txn.statement_id!,
              pageNumber: txn.source_page!,
              sourceFile: txn.source_file,
            })}
            className="flex items-center gap-1 text-xs text-green-700 hover:text-green-900 hover:underline font-medium transition-colors"
            title={fullCitation}
          >
            <Eye size={12} className="shrink-0" />
            <span>{shortCitation}</span>
          </button>
        ) : (
          <span className="text-xs text-ink-faint">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-xs text-right whitespace-nowrap align-top">
        <Money value={Math.abs(txn.amount)} className="font-semibold text-danger" />
      </td>
    </tr>
  );
}

export default function MissingAccountsTab({ leads }: MissingAccountsTabProps) {
  const [viewer, setViewer] = useState<ViewerState | null>(null);

  const totalAmount = leads.reduce((s, l) => s + l.total_amount, 0);
  const totalTransfers = leads.reduce((s, l) => s + l.transfer_count, 0);

  return (
    <>
      <div className="space-y-5">
        <div className="rounded-lg border border-clay-200 bg-clay-50 px-5 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <AlertTriangle size={20} className="text-clay-700 shrink-0 mt-px" />
              <div>
                <h3 className="t-label text-clay-900">
                  Money was transferred to {leads.length} account{leads.length !== 1 ? 's' : ''} with no statements on file.
                </h3>
                <p className="text-xs text-clay-700 mt-1">
                  Expand each account to see individual transactions with citations. Click the <strong>eye icon</strong> on any row to view the source page from the original statement. Use <strong>Copy for Discovery</strong> to copy a citation-complete transaction list.
                </p>
              </div>
            </div>
            <CopyButton text={buildAllText(leads)} label="Copy All Accounts" />
          </div>

          <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-clay-200">
            <MetricCard
              label="Total Unaccounted"
              icon={<DollarSign size={16} />}
              value={<Money value={Math.abs(totalAmount)} whole className="text-danger" />}
            />
            <MetricCard
              label="Missing Accounts"
              icon={<Landmark size={16} />}
              value={leads.length}
            />
            <MetricCard
              label="Total Transfers"
              icon={<ArrowUpRight size={16} />}
              value={totalTransfers}
            />
          </div>
        </div>

        <div className="space-y-3">
          {leads.map(lead => (
            <LeadCard
              key={`${lead.account_type}-${lead.last4}`}
              lead={lead}
              onViewSource={setViewer}
            />
          ))}
        </div>
      </div>

      {viewer && (
        <SourcePageViewer
          statementId={viewer.statementId}
          pageNumber={viewer.pageNumber}
          sourceFile={viewer.sourceFile}
          isOpen={true}
          onClose={() => setViewer(null)}
        />
      )}
    </>
  );
}
